import { __assign } from "tslib";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState, } from "react";
import isEmpty from "lodash/isEmpty";
import pickBy from "lodash/pickBy";
import find from "lodash/find";
import uniq from "lodash/uniq";
import keyBy from "lodash/keyBy";
import { LinedPangeaDataGrid, useGridSchemaColumns, } from "@pangeacyber/react-mui-shared";
import { ObjectStore } from "../../types";
import { StoreViewerFields, StoreViewerFieldsWithID, } from "../StoreDataGrid/fields";
import { useStoreFileViewerContext, useStoreFileViewerFolder, } from "../../hooks/context";
import FolderPath from "../StoreDataGrid/FolderPath";
import { Stack, Chip, LinearProgress } from "@mui/material";
import DownloadFileOptions from "./DownloadFileOptions";
import MultiSelectMenu from "../StoreDataGrid/MultiSelectMenu";
import DataGridParentStack from "../StoreDataGrid/DataGridParentStack";
import UploadPopover from "../UploadPopover";
import { downloadFile } from "../../utils/file";
import DownloadPopover from "../DownloadPasswordPopover";
import BucketSelector from "../StoreDataGrid/BucketSelector";
export var DEFAULT_VISIBILITY_MODEL = {
    name: true,
    size: true,
    updated_at: true,
};
export var DEFAULT_COLUMN_ORDER = ["name", "size", "updated_at"];
var StoreDownloadDataGrid = function (_a) {
    var _b, _c, _d;
    var defaultVisibilityModel = _a.defaultVisibilityModel, defaultColumnOrder = _a.defaultColumnOrder, customizations = _a.customizations, includeIdColumn = _a.includeIdColumn, virtualRoot = _a.virtualRoot;
    var _e = useState(true), firstLoad = _e[0], setFirstLoad = _e[1];
    var _f = useStoreFileViewerContext(), data = _f.data, request = _f.request, reload = _f.reload, loading = _f.loading, previewId = _f.previewId, apiRef = _f.apiRef;
    var _g = useStoreFileViewerFolder(), folder = _g.folder, setFolder = _g.setFolder, setParentId = _g.setParentId;
    var filters = request.filters, setFilters = request.setFilters, page = request.page, setPage = request.setPage, pageSize = request.pageSize, setPageSize = request.setPageSize, lastKnownPage = request.lastKnownPage, sorting = request.sorting, setSorting = request.setSorting;
    var columns_ = useGridSchemaColumns(includeIdColumn ? StoreViewerFieldsWithID : StoreViewerFields);
    var columns = useMemo(function () {
        return columns_.map(function (column_) {
            var _a;
            var override = (_a = customizations === null || customizations === void 0 ? void 0 : customizations.columnOverrides) === null || _a === void 0 ? void 0 : _a[column_.field];
            if (!override)
                return column_;
            return __assign(__assign({}, column_), override);
        });
    }, [columns_, customizations]);
    var _h = useState(null), contextMenu = _h[0], setContextMenu = _h[1];
    var _j = useState([]), selected = _j[0], setSelected = _j[1];
    var setMultiSelected = useCallback(function (update) {
        setSelected(function (state) {
            var _a;
            var newState = state;
            if (typeof update === "function") {
                newState = update(state);
            }
            else {
                newState = update;
            }
            var objectMap = keyBy((_a = data.objects) !== null && _a !== void 0 ? _a : [], "id");
            return uniq(newState).filter(function (objectId) { return !!objectMap[objectId]; });
        });
    }, [data, setSelected]);
    var multiSelected = useMemo(function () {
        var _a;
        var objectMap = keyBy((_a = data.objects) !== null && _a !== void 0 ? _a : [], "id");
        return selected.filter(function (objectId) { return !!objectMap[objectId]; });
    }, [selected, data]);
    var handleGetRowClassName = function (params) {
        var selected = find(multiSelected, function (id) { return id === params.row.id; });
        if (selected) {
            return "Mui-selected";
        }
        return "";
    };
    var handleContextMenu = function (event) {
        if (!multiSelected)
            return;
        var foundParent = false;
        var rowEl = event.currentTarget.closest(".MuiDataGrid-row");
        if (rowEl) {
            var row = Number(rowEl.getAttribute("data-rowindex"));
            if (!Number.isNaN(row) && row >= 0 && row < data.objects.length) {
                var object_1 = data.objects[row];
                if (event.shiftKey || multiSelected.includes(object_1.id)) {
                    setMultiSelected(function (state) { return state.concat([object_1.id]); });
                }
                else {
                    setMultiSelected([object_1.id]);
                }
                foundParent = true;
            }
        }
        if (!foundParent && !multiSelected.length)
            return;
        event.preventDefault();
        setContextMenu(contextMenu === null
            ? {
                mouseX: event.clientX + 2,
                mouseY: event.clientY - 6,
            }
            : // repeated contextmenu when it is already open closes it with Chrome 84 on Ubuntu
                // Other native context menus might behave different.
                // With this behavior we prevent contextmenu from the backdrop to re-locale existing context menus.
                null);
    };
    var _k = useState(false), downloading = _k[0], setDownloading = _k[1];
    var handleDownloadFile = function (id) {
        var _a;
        if (downloading || !id)
            return;
        var objectMap = keyBy((_a = data.objects) !== null && _a !== void 0 ? _a : [], "id");
        var object = objectMap[id];
        if (!(object === null || object === void 0 ? void 0 : object.id))
            return;
        setDownloading(true);
        return downloadFile(object, apiRef)
            .then(function () {
            setDownloading(false);
        })
            .catch(function (err) {
            setDownloading(false);
        });
    };
    var isFiltered = !isEmpty(pickBy(filters !== null && filters !== void 0 ? filters : {}, function (v, k) { return !!v && k !== "parent_id"; }));
    var visibilityModel = useMemo(function () {
        return (defaultVisibilityModel !== null && defaultVisibilityModel !== void 0 ? defaultVisibilityModel : __assign(__assign({}, DEFAULT_VISIBILITY_MODEL), { parent_id: isFiltered ? true : false }));
    }, [defaultVisibilityModel, columns, isFiltered]);
    useEffect(function () {
        // For showing a single shared folder as the root,
        // auto-select top-level folder if virtualRoot flag is set
        if (virtualRoot && data.count === 1) {
            var obj = data.objects[0];
            if (obj.type === "folder" && obj.folder === "/") {
                setFolder("/" + data.objects[0].name);
            }
        }
        else if (!virtualRoot || data.last !== "") {
            setFirstLoad(false);
        }
    }, [data]);
    var rowCount = (_d = (_b = data === null || data === void 0 ? void 0 : data.count) !== null && _b !== void 0 ? _b : (_c = data === null || data === void 0 ? void 0 : data.objects) === null || _c === void 0 ? void 0 : _c.length) !== null && _d !== void 0 ? _d : 0;
    return (_jsxs(Stack, { spacing: 0, children: [loading && (_jsx(LinearProgress, { color: "info", sx: {
                    marginTop: "4px!important",
                    height: "2px",
                    marginBottom: "4px!important",
                } })), _jsx(LinedPangeaDataGrid, { columns: columns, data: firstLoad ? [] : data.objects, onRowDoubleClick: function (params) {
                    if (params.row.type === ObjectStore.ObjectType.Folder) {
                        setParentId(params.row.id);
                        return false;
                    }
                    else {
                        handleDownloadFile(params.row.id);
                    }
                }, onRowClick: function (params, event) {
                    var _a;
                    if (event.shiftKey) {
                        event.preventDefault();
                        (_a = document.getSelection()) === null || _a === void 0 ? void 0 : _a.removeAllRanges();
                        setMultiSelected(function (state) { return state.concat([params.row.id]); });
                    }
                    else {
                        setMultiSelected([params.row.id]);
                    }
                }, DataGridProps: __assign({}, (!!apiRef.getArchive && {
                    getRowClassName: handleGetRowClassName,
                    slotProps: {
                        row: {
                            onContextMenu: handleContextMenu,
                            style: { cursor: "context-menu" },
                        },
                    },
                })), ActionColumn: {
                    render: function (object) { return _jsx(DownloadFileOptions, { data: object }); },
                    isPinned: true,
                    GridColDef: {
                        minWidth: 150,
                    },
                }, loading: loading, header: _jsxs(Stack, { direction: "row", alignItems: "center", justifyContent: "space-between", width: "100%", children: [_jsxs(Stack, { direction: "row", alignItems: "center", spacing: 1, sx: { paddingBottom: 1, marginLeft: -0.5 }, children: [_jsx(BucketSelector, {}), _jsx(FolderPath, { defaultHidden: true, virtualRoot: true })] }), multiSelected.length >= 1 && (_jsx(Chip, { size: "small", label: "".concat(multiSelected.length, " Selected"), onDelete: function () {
                                setMultiSelected([]);
                            } }))] }), ServerPagination: {
                    page: page,
                    onPageChange: function (page) { return setPage(page); },
                    pageSize: pageSize,
                    onPageSizeChange: setPageSize,
                    rowsPerPageOptions: [5, 10, 20, 50],
                    paginationRowCount: Math.min(lastKnownPage * pageSize, rowCount),
                    rowCount: rowCount,
                }, ServerSorting: {
                    // @ts-ignore "asc" | "desc" | undefined
                    sort: sorting.order,
                    sortBy: sorting.order_by,
                    onSortChange: function (sort, sortBy) {
                        setSorting({
                            order: sort,
                            order_by: sortBy,
                        });
                    },
                }, components: (apiRef === null || apiRef === void 0 ? void 0 : apiRef.upload)
                    ? {
                        DataGridParentStack: DataGridParentStack,
                    }
                    : undefined }), !!(apiRef === null || apiRef === void 0 ? void 0 : apiRef.upload) && _jsx(UploadPopover, {}), _jsx(DownloadPopover, {}), _jsx(MultiSelectMenu, { selected: multiSelected, contextMenu: contextMenu, setContextMenu: setContextMenu })] }));
};
export default StoreDownloadDataGrid;
